import { useCallback, useEffect, useMemo, useState } from "react";
import { useUserContext } from "../hooks/UserContext";
import { useNotification } from "../hooks/NotificationContext";
import { Cliente } from "../models/ClienteModel";
import { Producto } from "../models/ProductoModel";
import { LineaVenta } from "../models/LineaVentaModel";
import { fetchGetClientes } from "../services/clienteService";
import { fetchGetProductosParaVenta } from "../services/productoService";
import { Button, CircularProgress, Dialog, Grid, Box, Typography } from "@mui/material";
import DynamicGrid from "../components/DynamicTable";
import { fetchGuardarVenta } from "../services/ventaService";
import PersonIcon from '@mui/icons-material/Person';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DeleteIcon from "@mui/icons-material/Delete";
import { requestResultModel } from "../models/RequestReturnModel";
import LocalSelector from "./components/LocalSelector";
import { isAbortError } from "../services/fetchService";


export const gridColumnDefinitions = {
  lineaVentas: {
    'PRODUCTO.PRODUCTO': { Header: 'Producto', Editable: false },
    'PRODUCTO.CONTADO': { Header: '$', Editable: false, IsNumber: true },
    'CANTIDAD': { Header: 'Cantidad', Editable: true, IsNumber: true },
    'DELETE': { Header: 'Eliminar', Editable: false, CustomCell: true }, // Custom column for delete action
  },
  productos: {
    'PRODUCTO': { Header: 'Producto', Editable: false },
    'CODIGO': { Header: 'Codigo', Editable: false },
    'CONTADO': { Header: '$ Unitario', Editable: false, IsNumber: true },
  },
  clientes: {
    'CLIENTE': { Header: 'Cliente', Editable: false },
  },
};

const OrdenDeVenta = () => {
  const { getUser, user } = useUserContext();
  const { showNotification } = useNotification();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [idLocal, setIdLocal] = useState<string | undefined>(undefined);
  const [productos, setProductos] = useState<Producto[]>([]);
  const [selectDialogOpen, setSelectDialogOpen] = useState<boolean>(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState<Cliente | undefined>(undefined);
  const [lineaVenta, setLineaVenta] = useState<LineaVenta[]>([]);
  const [data, setDialogData] = useState<any[]>([]);
  const [seleccionandoProducto, setSeleccionandoProducto] = useState<boolean>(false);
  const [seleccionandoCliente, setSeleccionandoCliente] = useState<boolean>(false);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const showMessage = useCallback((message: string, severity: "success" | "info" | "warning" | "error") => {
    showNotification(message, severity);
  }, [showNotification]);


  const handleSelectClienteDialog = () => {
    setSeleccionandoProducto(false);
    setSeleccionandoCliente(true);
    setDialogData(clientes);
    setSelectDialogOpen(!selectDialogOpen);
  }

  const handleSelectProductoDialog = () => {
    if (!idLocal) {
      showMessage("Debe seleccionar un local primero", 'error');
      return;
    }

    if (productos.length === 0) {
      showMessage("Cargando productos...", 'info');
      return;
    }

    setSeleccionandoCliente(false);
    setSeleccionandoProducto(true);
    setDialogData(productos);
    setSelectDialogOpen(!selectDialogOpen);
  }

  const userLoggedIn = useMemo(() => user ?? getUser(), [getUser, user]);

  useEffect(() => {
    if (userLoggedIn) {
      const controller = new AbortController();
      fetchGetClientes(userLoggedIn!, controller.signal).then((clis: Cliente[]) => {
        setClientes(clis);
      }).catch(err => {
        if (isAbortError(err)) return;
        console.log(err);
      })

      return () => controller.abort();
    }
  }, [userLoggedIn]);

  const eventHandler = (params: any) => {
    try {
      if (seleccionandoCliente) {
        const row = params.row as Cliente;
        setClienteSeleccionado(row);
      }
      if (seleccionandoProducto) {
        const row = params.row as Producto;
        setLineaVenta([...lineaVenta, { producto: { ...row }, cantidad: 1 }])
      }
      setSelectDialogOpen(false);
    } catch { }
    // const companyName = row.companyName;
    // const role = row.role;
    // const roleToActivate = userRolesAndCompanies?.find(r => r.companyName === companyName && r.role === role);
    // activateRole(roleToActivate ?? undefined);
  }

  const handleDeleteRow = (rowIndex: number) => {
    const updatedLineaVenta = [...lineaVenta];
    updatedLineaVenta.splice(rowIndex, 1); // Remove the row at the specified index
    setLineaVenta(updatedLineaVenta);
  };

  const guardarVenta = () => {
    if (!userLoggedIn || !clienteSeleccionado || !idLocal || isNaN(Number(idLocal))) {
      showMessage("Error al guardar la venta", 'error');
      return;
    }
    if (lineaVenta.length === 0) {
      showMessage("No hay productos en la venta", 'error');
      return;
    }

    fetchGuardarVenta(userLoggedIn!, clienteSeleccionado!.ID, Number(idLocal), lineaVenta).then((res: requestResultModel) => {
      showMessage(res.message, res.success ? 'success' : 'error');
      setLineaVenta([]);
      setClienteSeleccionado(undefined);
    }).catch(err => {
      showMessage("Error al guardar la venta", 'error');
      console.log(err);
    });
  }

  const handleLocalChange = (idLocal: string | undefined) => {
    setIdLocal(idLocal);
  };

  useEffect(() => {
    if (userLoggedIn && idLocal) {
      const controller = new AbortController();
      setLoadingProductos(true);
      fetchGetProductosParaVenta(idLocal, userLoggedIn!, controller.signal)
        .then((prods: Producto[]) => {
          setProductos(prods);
        })
        .catch(err => {
          if (isAbortError(err)) return;
          console.log(err);
          showMessage("Error al cargar productos", 'error');
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoadingProductos(false);
        });

      return () => controller.abort();
    }
  }, [idLocal, showMessage, userLoggedIn]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Orden de Venta
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Typography variant="h6" gutterBottom>
            Seleccione un Local para la venta:
          </Typography>
        </Grid>
        <Grid item xs={12} sm={2}>
          <LocalSelector onLocalChange={handleLocalChange} />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Button
            onClick={handleSelectClienteDialog}
            variant="contained"
            color="primary"
            fullWidth
            sx={{ textTransform: "none", fontWeight: "bold", py: 1.5 }}
            startIcon={<PersonIcon />}
          >
            Seleccionar Cliente
          </Button>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button
            onClick={handleSelectProductoDialog}
            variant="contained"
            color="secondary"
            fullWidth
            disabled={loadingProductos || !idLocal} // Disable button while loading
            sx={{ textTransform: "none", fontWeight: "bold", py: 1.5 }}
            startIcon={loadingProductos ? <CircularProgress color="inherit" size={18} /> : <ShoppingCartIcon />}
          >
            {loadingProductos ? "Cargando..." : "Seleccionar Producto"}
          </Button>
        </Grid>
      </Grid>

      {/* Dialog */}
      <Dialog 
        open={selectDialogOpen} 
        fullWidth 
        keepMounted
        onClose={() => setSelectDialogOpen(false)}
      >
        <Typography variant="h4">
          {seleccionandoCliente ? "Seleccionar Cliente" : "Seleccionar Producto"}
        </Typography>
        <DynamicGrid
          data={data}
          customHeaderMap={
            seleccionandoCliente
              ? gridColumnDefinitions.clientes
              : gridColumnDefinitions.productos
          }
          pageSize={5}
          removeIfNotCustom={true}
          rowClickEventHandler={eventHandler}
        />
      </Dialog>
      <div>Orden de Venta para: {clienteSeleccionado?.CLIENTE}</div>
      <DynamicGrid
        data={lineaVenta.map((linea, index) => ({
          ...linea, // Spread the existing properties
          DELETE: "",
        }))} // Add the DELETE property to each row
        customHeaderMap={gridColumnDefinitions.lineaVentas}
        pageSize={5}
        removeIfNotCustom={true}
        customRenderers={{
          DELETE: (row, rowIndex) => (
            <Button
              onClick={() => handleDeleteRow(rowIndex)}
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
            >
              Eliminar
            </Button>
          ),
        }}
      />
      <Box sx={{ mt: 3, textAlign: "right" }}>
        <Button
          onClick={guardarVenta}
          variant="contained"
          color="success"
          sx={{ textTransform: "none", fontWeight: "bold", px: 4, py: 1.5 }}
        >
          Guardar Venta
        </Button>
      </Box>
    </Box>
  );
}

export default OrdenDeVenta;



